import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { PaziaFormButton, PaziaInput, PaziaLoader } from "components/atoms";
import { useButtonStyles, shopApi, ButtonStyle } from "api/shop";
import { useToaster } from "utils";
import { useState, useEffect } from "react";
import { MdAdd, MdDelete } from "react-icons/md";

export const ShopButtonStylesForm = () => {
  const { buttonStyles, isLoading, invalidate } = useButtonStyles();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      styles: buttonStyles.length > 0 ? buttonStyles : getDefaultStyles(),
    },
  });
  const { handleSubmit, reset, control, watch } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "styles",
  });

  const watchedStyles = watch("styles");

  useEffect(() => {
    if (buttonStyles.length > 0) {
      reset({ styles: buttonStyles });
    }
  }, [buttonStyles, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      await shopApi.updateButtonStyles(data.styles);
      showSuccess("Zapisano style przycisków");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać stylów przycisków");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const addStyle = () => {
    const currentStyles = watchedStyles || [];
    const nextId = currentStyles.length > 0
      ? Math.max(...currentStyles.map((s) => s.id)) + 1
      : 1;
    append({
      id: nextId,
      name: `style-${nextId}`,
      label: `Styl ${nextId}`,
      bgColor: "#3b82f6",
      textColor: "#ffffff",
      borderColor: "",
      hoverBgColor: "#2563eb",
      hoverTextColor: "",
    });
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-4xl gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200">Style przycisków</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Zdefiniuj style przycisków używane w sekcjach landing page
            </p>
          </div>
          <button
            type="button"
            onClick={addStyle}
            className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
          >
            <MdAdd size={18} />
            Dodaj styl
          </button>
        </div>

        {fields.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Brak stylów. Kliknij "Dodaj styl", aby dodać.</p>
        ) : (
          <div className="grid gap-4">
            {fields.map((field, index) => {
              const style = watchedStyles?.[index];
              return (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Styl #{index + 1}</span>
                    <div className="flex items-center gap-3">
                      {/* Preview button */}
                      <button
                        type="button"
                        className="rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                        style={{
                          backgroundColor: style?.bgColor || "#3b82f6",
                          color: style?.textColor || "#ffffff",
                          border: style?.borderColor ? `2px solid ${style.borderColor}` : "none",
                        }}
                      >
                        Podgląd
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        title="Usuń styl"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <PaziaInput name={`styles.${index}.name`} label="Nazwa (slug)" placeholder="primary" required />
                    <PaziaInput name={`styles.${index}.label`} label="Etykieta" placeholder="Primary" required />
                    <PaziaInput name={`styles.${index}.bgColor`} label="Kolor tła" placeholder="#3b82f6" required />
                    <PaziaInput
                      name={`styles.${index}.textColor`}
                      label="Kolor tekstu"
                      placeholder="#ffffff"
                      required
                    />
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    <PaziaInput
                      name={`styles.${index}.borderColor`}
                      label="Kolor obramowania"
                      placeholder="#3b82f6 (opcjonalnie)"
                    />
                    <PaziaInput
                      name={`styles.${index}.hoverBgColor`}
                      label="Kolor tła (hover)"
                      placeholder="#2563eb (opcjonalnie)"
                    />
                    <PaziaInput
                      name={`styles.${index}.hoverTextColor`}
                      label="Kolor tekstu (hover)"
                      placeholder="#ffffff (opcjonalnie)"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};

const getDefaultStyles = (): ButtonStyle[] => [
  { id: 1, name: "primary", label: "Primary", bgColor: "#3b82f6", textColor: "#ffffff", hoverBgColor: "#2563eb" },
  { id: 2, name: "secondary", label: "Secondary", bgColor: "#f1f5f9", textColor: "#334155", hoverBgColor: "#e2e8f0" },
  {
    id: 3,
    name: "outline",
    label: "Outline",
    bgColor: "transparent",
    textColor: "#3b82f6",
    borderColor: "#3b82f6",
    hoverBgColor: "#eff6ff",
  },
];

const styleSchema = yup.object({
  id: yup.number().required(),
  name: yup.string().required("Nazwa jest wymagana"),
  label: yup.string().required("Etykieta jest wymagana"),
  bgColor: yup.string().required("Kolor tła jest wymagany"),
  textColor: yup.string().required("Kolor tekstu jest wymagany"),
  borderColor: yup.string().nullable(),
  hoverBgColor: yup.string().nullable(),
  hoverTextColor: yup.string().nullable(),
});

const schema = yup
  .object({
    styles: yup.array().of(styleSchema).min(1, "Dodaj przynajmniej jeden styl").required(),
  })
  .required();

interface FormValues {
  styles: ButtonStyle[];
}
